import { useAuth } from "services/Auth";
import { useCallback, useEffect, useState } from "react";
import { clearSelectedServiceForStaff, getSelectedServiceForStaff, setSelectedServiceForStaff } from "utils/offlineCache";

const useSelectedService = () => {
  const { auth } = useAuth();
  const staffId = auth?.data?._id || null;
  const [selectedService, setSelectedServiceState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshSelectedService = useCallback(async () => {
    if (!staffId) {
      setSelectedServiceState(null);
      setError(null);
      setLoading(false);
      return { status: true, data: null };
    }

    setLoading(true);
    const selectedServiceResponse = await getSelectedServiceForStaff({ staff_id: staffId });
    if (selectedServiceResponse.status === false) {
      setError(selectedServiceResponse.data);
      setSelectedServiceState(null);
      setLoading(false);
      return selectedServiceResponse;
    }

    setSelectedServiceState(selectedServiceResponse.data || null);
    setError(null);
    setLoading(false);
    return selectedServiceResponse;
  }, [staffId]);

  const setSelectedService = useCallback(
    async (service) => {
      if (!staffId) {
        return { status: false, data: "Staff ID is required" };
      }
      if (!service || typeof service !== "object") {
        return { status: false, data: "Service is required" };
      }

      const setSelectedServiceResponse = await setSelectedServiceForStaff({ staff_id: staffId, service });
      if (setSelectedServiceResponse.status === false) {
        setError(setSelectedServiceResponse.data);
        return setSelectedServiceResponse;
      }

      setSelectedServiceState(service);
      setError(null);
      return { status: true, data: service };
    },
    [staffId],
  );

  const clearSelectedService = useCallback(async () => {
    if (!staffId) {
      setSelectedServiceState(null);
      setError(null);
      return { status: true, data: "OK" };
    }

    const clearSelectedServiceResponse = await clearSelectedServiceForStaff({ staff_id: staffId });
    if (clearSelectedServiceResponse.status === false) {
      setError(clearSelectedServiceResponse.data);
      return clearSelectedServiceResponse;
    }

    setSelectedServiceState(null);
    setError(null);
    return { status: true, data: "OK" };
  }, [staffId]);

  useEffect(() => {
    let active = true;

    const loadSelectedService = async () => {
      const selectedServiceResponse = await refreshSelectedService();
      if (active === false) {
        return;
      }
      if (selectedServiceResponse.status === false) {
        setError(selectedServiceResponse.data);
      }
    };

    loadSelectedService();

    return () => {
      active = false;
    };
  }, [refreshSelectedService]);

  return {
    selectedService,
    loading,
    error,
    setSelectedService,
    clearSelectedService,
    refreshSelectedService,
  };
};

export default useSelectedService;
